import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, PlusCircle, Save, X, Trash2, FolderOpen, ChevronRight, LayoutGrid, List, ChevronDown, Mic, Square, Check, AlertCircle, BookOpen, SlidersHorizontal, RefreshCw, ImagePlus, Users, Pause, Play, Edit2 } from 'lucide-react';
import axios from 'axios';
import { DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors, DragOverlay } from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useRecordingContext } from '../contexts/RecordingContext';
import { useNavigationDirection } from '../contexts/NavigationContext';
import SwipeToDelete from './SwipeToDelete';
import { ProgressBar } from './ui/ProgressBar';
import { useMobileDisplayCheck } from '../hooks/useMobileDisplayCheck';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth handled via Bearer token in axios interceptor (App.js)

// Direction-aware page variants for swipe transitions
const getPageVariants = (direction) => ({
  initial: { opacity: 0, x: direction * 30 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: direction * -30 }
});

const pageTransition = { duration: 0.25, ease: 'easeOut' };

// Wobble animation for newly shared folders
const wobbleAnimation = {
  initial: { rotate: 0 },
  animate: {
    rotate: [0, -3, 3, -3, 3, 0],
    transition: { duration: 0.5, ease: "easeInOut", delay: 0.3 }
  }
};

// Helper: hex to rgba
function hexToRgba(hex, alpha) {
  if (!hex) return 'transparent';
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// Sortable folder tile wrapper for drag-and-drop
function SortableFolderTile({ id, children, disabled }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 50 : 'auto',
    position: 'relative',
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </div>
  );
}

function StoriesPage({ user }) {
  const location = useLocation();
  const { direction } = useNavigationDirection();
  
  // Mobile display check - auto-reload if page doesn't render properly
  useMobileDisplayCheck('[data-testid="stories-page-title"]');
  // Initialize from localStorage cache for instant display
  const [folders, setFolders] = useState(() => {
    try { const c = localStorage.getItem('noyo_folders_cache'); return c ? JSON.parse(c) : []; } catch { return []; }
  });
  const [experiences, setExperiences] = useState([]);
  const [totalStoryCountFromServer, setTotalStoryCountFromServer] = useState(() => {
    try { return parseInt(localStorage.getItem('noyo_total_count_cache') || '0', 10); } catch { return 0; }
  });
  const [totalNewStories, setTotalNewStories] = useState(0);
  const [newFolderName, setNewFolderName] = useState('');
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderType, setNewFolderType] = useState('topic');
  const [showAddMenu, setShowAddMenu] = useState(false);
  const addMenuRef = useRef(null);
  const [isLoadingData, setIsLoadingData] = useState(() => {
    try { return !localStorage.getItem('noyo_folders_cache'); } catch { return true; }
  });
  const [deleteFolderConfirm, setDeleteFolderConfirm] = useState(null);
  const [isGeneratingSuggestions, setIsGeneratingSuggestions] = useState(false);
  const [confirmingFolderId, setConfirmingFolderId] = useState(null);
  const [renamingSuggestedId, setRenamingSuggestedId] = useState(null);
  const [renamingSuggestedName, setRenamingSuggestedName] = useState('');
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem('noyo_stories_view') || 'grid'; } catch (e) { return 'grid'; }
  });
  
  // Handler to update view mode and persist to localStorage
  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    try { localStorage.setItem('noyo_stories_view', mode); } catch (e) { /* localStorage unavailable */ }
  };
  
  const [showSuggestions, setShowSuggestions] = useState(true); // Auto-show ghost tiles for AI suggestions
  const [quickStory, setQuickStory] = useState(() => {
    try { return localStorage.getItem('noyo_story_draft') || ''; } catch (e) { return ''; }
  });
  // State for newly shared folder highlight
  const [newSharedFolderId, setNewSharedFolderId] = useState(null);
  const [newSharedFolderOwner, setNewSharedFolderOwner] = useState(null);
  const [showSharedFolderTip, setShowSharedFolderTip] = useState(false);
  const sharedFolderRef = useRef(null);

  // Drag-and-drop state
  const [activeId, setActiveId] = useState(null);
  const [overFolderId, setOverFolderId] = useState(null);
  const [nestReady, setNestReady] = useState(false); // true when held over a folder long enough to nest
  const nestTimerRef = useRef(null);
  const pointerSensor = useSensor(PointerSensor, { activationConstraint: { delay: 400, tolerance: 8 } });
  const touchSensor = useSensor(TouchSensor, { activationConstraint: { delay: 400, tolerance: 8 } });
  const sensors = useSensors(pointerSensor, touchSensor);

  const updateQuickStory = (val) => {
    setQuickStory(val);
    try { if (val) localStorage.setItem('noyo_story_draft', val); else localStorage.removeItem('noyo_story_draft'); } catch (e) { /* localStorage unavailable */ }
  };
  const [quickStoryFolder, setQuickStoryFolder] = useState('');
  const [quickStoryFolders, setQuickStoryFolders] = useState([]);
  const [showInlineNewFolder, setShowInlineNewFolder] = useState(false);
  const [inlineNewFolderName, setInlineNewFolderName] = useState('');
  const [isAddingStory, setIsAddingStory] = useState(false);
  const [folderCreatedToast, setFolderCreatedToast] = useState(null);
  const [showBottomNewFolder, setShowBottomNewFolder] = useState(false);
  const [bottomFolderName, setBottomFolderName] = useState('');
  const [noSuggestionsMsg, setNoSuggestionsMsg] = useState('');
  const [showJournalPrompt, setShowJournalPrompt] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [storyCount, setStoryCount] = useState(0);
  const [streak, setStreak] = useState(null);
  const [sortBy, setSortBy] = useState('recent'); // 'name', 'count', 'recent', 'shared' - default to latest edit
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [filterShared, setFilterShared] = useState(false);
  const sortMenuRef = useRef(null);
  
  // Global recording context - disables swipe navigation while recording
  const { setIsRecording: setGlobalRecording } = useRecordingContext();
  
  // Audio recording in quick story
  const [isRecordingQuick, setIsRecordingQuick] = useState(false);
  const [isPausedQuick, setIsPausedQuick] = useState(false);
  const [isTranscribingQuick, setIsTranscribingQuick] = useState(false);
  const [transcriptionProgress, setTranscriptionProgress] = useState(0);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [quickAudioBlob, setQuickAudioBlob] = useState(null);
  const [quickImage, setQuickImage] = useState(null);
  const [quickImages, setQuickImages] = useState([]); // [{file, preview}]
  const [saveProgress, setSaveProgress] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const quickMediaRecorderRef = useRef(null);
  const quickAudioChunksRef = useRef([]);
  const quickStreamRef = useRef(null);
  const recordingTimerRef = useRef(null);
  const navigate = useNavigate();

  // Handle generate suggestions - show panel when clicked
  const handleGenerateSuggestions = async () => {
    setShowSuggestions(true);
    setIsGeneratingSuggestions(true);
    setNoSuggestionsMsg('');
    try {
      // Remove existing suggested folders so regenerate produces fresh results
      const currentSuggested = folders.filter(f => f.is_suggested);
      for (const s of currentSuggested) {
        try { await axios.delete(`${API}/folders/suggested/${s.id}`); } catch (e) { /* Suggested folder already removed */ }
      }
      const res = await axios.post(`${API}/folders/suggest`);
      await fetchData();
      if (res.data.suggestions && res.data.suggestions.length === 0) {
        setNoSuggestionsMsg('No new folder suggestions — try adding more journal entries first.');
        setTimeout(() => setNoSuggestionsMsg(''), 4000);
      }
    } catch (error) {
      console.error('Error generating suggestions:', error);
      setNoSuggestionsMsg('Failed to generate suggestions. Please try again.');
      setTimeout(() => setNoSuggestionsMsg(''), 4000);
    } finally {
      setIsGeneratingSuggestions(false);
    }
  };

  // Hide suggestions when navigating away
  useEffect(() => {
    return () => {
      setShowSuggestions(false);
    };
  }, []);

  const [sharedFolders, setSharedFolders] = useState([]);
  const [mySharedInfo, setMySharedInfo] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  // Handle navigation state for newly shared folder
  useEffect(() => {
    if (location.state?.newSharedFolderId) {
      setNewSharedFolderId(location.state.newSharedFolderId);
      setShowSharedFolderTip(location.state.showSharedFolderTip || false);
      // Clear the state so it doesn't persist on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Scroll to and highlight the newly shared folder
  useEffect(() => {
    if (newSharedFolderId && sharedFolders.length > 0 && sharedFolderRef.current) {
      // Find the owner name for the instruction tip
      const sharedFolder = sharedFolders.find(sf => sf.folder_id === newSharedFolderId);
      if (sharedFolder) {
        setNewSharedFolderOwner(sharedFolder.owner_name);
      }
      // Scroll to the shared folder with offset
      setTimeout(() => {
        sharedFolderRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
      // Auto-dismiss tip after 8 seconds
      const timer = setTimeout(() => {
        setShowSharedFolderTip(false);
        setNewSharedFolderId(null);
      }, 8000);
      return () => clearTimeout(timer);
    }
  }, [newSharedFolderId, sharedFolders]);

  // Close sort menu and add menu on click outside
  useEffect(() => {
    const handleClick = (e) => {
      if (sortMenuRef.current && !sortMenuRef.current.contains(e.target)) setShowSortMenu(false);
      if (addMenuRef.current && !addMenuRef.current.contains(e.target)) setShowAddMenu(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const fetchData = async () => {
    try {
      // Fetch folders first (fast) — show tiles immediately
      const foldersRes = await axios.get(`${API}/folders?include_counts=true`);
      const foldersData = foldersRes.data;
      if (foldersData.folders) {
        setFolders(foldersData.folders);
        setTotalStoryCountFromServer(foldersData.total_story_count || 0);
        setTotalNewStories(foldersData.total_new_stories || 0);
        // Cache for instant display on next visit
        try {
          localStorage.setItem('noyo_folders_cache', JSON.stringify(foldersData.folders));
          localStorage.setItem('noyo_total_count_cache', String(foldersData.total_story_count || 0));
        } catch { /* quota exceeded */ }
      } else {
        setFolders(foldersData);
        try { localStorage.setItem('noyo_folders_cache', JSON.stringify(foldersData)); } catch {}
      }
      setIsLoadingData(false);

      // Fetch secondary data in parallel (streak, shared, shared-info)
      const [streakRes, sharedRes, sharedInfoRes] = await Promise.all([
        axios.get(`${API}/streak`).catch(() => ({ data: null })),
        axios.get(`${API}/folders/shared-with-me`).catch(() => ({ data: [] })),
        axios.get(`${API}/folders/my-shared-info`).catch(() => ({ data: {} }))
      ]);
      if (streakRes.data) setStreak(streakRes.data);
      setSharedFolders(sharedRes.data);
      setMySharedInfo(sharedInfoRes.data || {});
    } catch (error) {
      console.error('Error fetching data:', error);
      setIsLoadingData(false);
    }
  };

  const createFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      const res = await axios.post(`${API}/folders`, { name: newFolderName, folder_type: newFolderType });
      setNewFolderName('');
      setShowNewFolder(false);
      setNewFolderType('topic');
      await fetchData();
      const typeLabel = newFolderType === 'event' ? 'event' : 'folder';
      setFolderCreatedToast(`New ${typeLabel} "${newFolderName}" created`);
      setTimeout(() => setFolderCreatedToast(null), 3000);
    } catch (error) {
      console.error('Error creating folder:', error);
    }
  };

  const handleCreateAndAssignFolder = async (folderName, context) => {
    try {
      await axios.post(`${API}/folders`, { name: folderName });
      await fetchData();
      if (context === 'stories') {
        setQuickStoryFolder(folderName);
      }
      setFolderCreatedToast(`New folder "${folderName}" created`);
      setTimeout(() => setFolderCreatedToast(null), 3000);
    } catch (error) {
      console.error('Error creating folder:', error);
    }
  };

  const getStoryCount = (folderName) => {
    // Use server-side counts if available (from include_counts=true)
    const folder = folders.find(f => f.name === folderName);
    if (folder && folder.story_count !== undefined) return folder.story_count;
    // Fallback to client-side filtering
    return experiences.filter(exp => exp.topic_folder === folderName).length;
  };

  const totalStoryCount = totalStoryCountFromServer || experiences.length;
  const unsortedCount = (() => {
    const unsortedFolder = folders.find(f => f.name === 'Unsorted Stories');
    if (unsortedFolder && unsortedFolder.story_count !== undefined) return unsortedFolder.story_count;
    return experiences.filter(exp => exp.topic_folder === 'Unsorted Stories').length;
  })();

  const handleQuickStory = async () => {
    if (!quickStory.trim() && quickImages.length === 0 && !quickAudioBlob) return;
    
    if (quickStory.trim()) {
      setIsAnalyzing(true);
      try {
        const analyzeRes = await axios.post(`${API}/experiences/analyze-content`, { text: quickStory });
        if (analyzeRes.data.is_multiple) {
          setStoryCount(analyzeRes.data.count || 2);
          setShowJournalPrompt(true);
          setIsAnalyzing(false);
          return;
        }
      } catch (err) {
        console.error('Analyze error:', err);
      }
      setIsAnalyzing(false);
    }
    
    await saveAsQuickStory();
  };

  const saveAsQuickStory = async () => {
    setIsAddingStory(true);
    setIsSaving(true);
    setSaveProgress(0);
    setShowJournalPrompt(false);
    try {
      const totalSteps = quickImages.length + (quickAudioBlob ? 1 : 0) + 2; // images + audio + save + sort
      let completedSteps = 0;
      const tick = () => { completedSteps++; setSaveProgress(Math.round((completedSteps / totalSteps) * 100)); };

      let imageData = [];
      for (const img of quickImages) {
        try {
          const formData = new FormData();
          formData.append('file', img.file);
          const uploadRes = await axios.post(`${API}/journal/upload-image`, formData);
          if (uploadRes.data.image) imageData.push(uploadRes.data.image);
        } catch (e) { console.error('Image upload error:', e); }
        tick();
      }
      
      let audioData = null;
      if (quickAudioBlob) {
        try {
          const { uploadResumableToGCS, getAudioDuration } = await import('../lib/gcsUpload');
          const contentType = quickAudioBlob.type || 'audio/webm';
          const filename = `recording.${contentType.split('/')[1] || 'webm'}`;
          const sessionResponse = await axios.post(`${API}/audio/create-upload-session`, {
            content_type: contentType, filename, size: quickAudioBlob.size
          });
          const { audio_id, object_path, session_url } = sessionResponse.data;
          const uploadResult = await uploadResumableToGCS(quickAudioBlob, session_url, () => {});
          if (uploadResult.success) {
            const duration = await getAudioDuration(quickAudioBlob);
            await axios.post(`${API}/audio/complete`, {
              audio_id, object_path, size: quickAudioBlob.size, content_type: contentType, duration_seconds: duration
            });
            audioData = { audio_id, audio_filename: object_path.split('/').pop(), object_path };
          }
        } catch (audioErr) { console.error('Audio upload error:', audioErr); }
        tick();
      }
      
      const selectedFolders = quickStoryFolders.length > 0 ? quickStoryFolders : (quickStoryFolder ? [quickStoryFolder] : []);
      const hasContent = quickStory.trim();
      const fallback = imageData.length > 0 ? '(image)' : '(audio)';
      
      await axios.post(`${API}/experiences`, {
        summary: hasContent ? quickStory : fallback,
        topic_folder: 'Unsorted Stories',
        images: imageData,
        audio_id: audioData?.audio_id,
        audio_filename: audioData?.audio_filename
      });
      
      for (const folder of selectedFolders.filter(f => f !== 'Unsorted Stories')) {
        await axios.post(`${API}/experiences`, {
          summary: hasContent ? quickStory : fallback,
          topic_folder: folder, images: imageData,
          audio_id: audioData?.audio_id, audio_filename: audioData?.audio_filename
        });
      }
      tick();
      
      if (quickStory.trim()) {
        const autoRes = await axios.post(`${API}/experiences/auto-sort`, { summary: quickStory });
        const autoSorted = autoRes.data.sorted_to || [];
        const newAutoFolders = autoSorted.filter(f => f !== 'Unsorted Stories' && !selectedFolders.includes(f));
        if (newAutoFolders.length > 0) {
          setFolderCreatedToast(`Saved to Unsorted + AI sorted to: ${newAutoFolders.join(', ')}`);
        } else {
          setFolderCreatedToast('Story saved to Unsorted Stories');
        }
      } else {
        setFolderCreatedToast('Story saved to Unsorted Stories');
      }
      tick();
      
      updateQuickStory('');
      setQuickStoryFolder('');
      setQuickStoryFolders([]);
      setQuickAudioBlob(null);
      quickImages.forEach(img => URL.revokeObjectURL(img.preview));
      setQuickImages([]);
      setQuickImage(null);
      setRecordingDuration(0);
      await fetchData();
      
      setTimeout(() => setFolderCreatedToast(null), 3000);
    } catch (error) {
      console.error('Error creating story:', error);
    } finally {
      setIsAddingStory(false);
      setIsSaving(false);
      setSaveProgress(0);
    }
  };

  const saveAsJournalEntry = async () => {
    setIsAddingStory(true);
    setShowJournalPrompt(false);
    try {
      const entryRes = await axios.post(`${API}/journal`, { content: quickStory });
      updateQuickStory('');
      setQuickStoryFolder('');
      setQuickStoryFolders([]);
      setQuickAudioBlob(null);
      quickImages.forEach(img => URL.revokeObjectURL(img.preview));
      setQuickImages([]);
      setQuickImage(null);
      setRecordingDuration(0);
      await fetchData();
      // Show toast with extraction results
      const extraction = entryRes.data?._extraction;
      if (extraction?.new_folder_suggestions?.length > 0) {
        const names = extraction.new_folder_suggestions.map(f => f.name).join(', ');
        setFolderCreatedToast(`✨ AI suggests new folder${extraction.new_folder_suggestions.length > 1 ? 's' : ''}: ${names}`);
        setShowSuggestions(true);
      } else if (extraction?.stories_extracted > 0) {
        setFolderCreatedToast(`Saved — ${extraction.stories_extracted} ${extraction.stories_extracted === 1 ? 'story' : 'stories'} extracted`);
      } else {
        setFolderCreatedToast('Saved as journal entry');
      }
      setTimeout(() => setFolderCreatedToast(null), 5000);
    } catch (error) {
      console.error('Error creating journal entry:', error);
    } finally {
      setIsAddingStory(false);
    }
  };

  const toggleQuickFolder = (folderName) => {
    setQuickStoryFolders(prev => 
      prev.includes(folderName) 
        ? prev.filter(f => f !== folderName) 
        : [...prev, folderName]
    );
  };

  // Audio recording for quick story
  const startQuickRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      quickStreamRef.current = stream;
      const mediaRecorder = new MediaRecorder(stream);
      quickMediaRecorderRef.current = mediaRecorder;
      quickAudioChunksRef.current = [];
      setRecordingDuration(0);
      
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) quickAudioChunksRef.current.push(e.data);
      };
      
      mediaRecorder.onstop = async () => {
        clearInterval(recordingTimerRef.current);
        const audioBlob = new Blob(quickAudioChunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(t => t.stop());
        setQuickAudioBlob(audioBlob);
        
        // Auto-transcribe with progress
        setIsTranscribingQuick(true);
        setTranscriptionProgress(0);
        
        // Simulate progress for better UX
        const progressInterval = setInterval(() => {
          setTranscriptionProgress(prev => prev >= 90 ? prev : prev + Math.random() * 15);
        }, 500);
        
        try {
          const formData = new FormData();
          formData.append('audio', audioBlob, 'quick_recording.webm');
          const res = await axios.post(`${API}/audio/transcribe`, formData);
          setTranscriptionProgress(100);
          if (res.data.text) {
            setQuickStory(prev => { const v = prev ? prev + '\n' + res.data.text : res.data.text; try { localStorage.setItem('noyo_story_draft', v); } catch(e) {} return v; });
          }
        } catch (err) {
          console.error('Transcription error:', err);
          setFolderCreatedToast('Transcription failed — you can still save with audio');
          setTimeout(() => setFolderCreatedToast(null), 3000);
        } finally {
          clearInterval(progressInterval);
          setIsTranscribingQuick(false);
          setTranscriptionProgress(0);
        }
      };
      
      mediaRecorder.start();
      setIsRecordingQuick(true);
      setGlobalRecording(true); // Disable swipe navigation
      recordingTimerRef.current = setInterval(() => {
        setRecordingDuration(prev => prev + 1);
      }, 1000);
    } catch (err) {
      console.error('Mic access denied:', err);
    }
  };

  const stopQuickRecording = () => {
    if (quickMediaRecorderRef.current && isRecordingQuick) {
      quickMediaRecorderRef.current.stop();
      setIsRecordingQuick(false);
      setIsPausedQuick(false);
      setGlobalRecording(false); // Re-enable swipe navigation
    }
  };

  const pauseQuickRecording = () => {
    if (quickMediaRecorderRef.current && isRecordingQuick && !isPausedQuick) {
      quickMediaRecorderRef.current.pause();
      setIsPausedQuick(true);
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    }
  };

  const resumeQuickRecording = () => {
    if (quickMediaRecorderRef.current && isRecordingQuick && isPausedQuick) {
      quickMediaRecorderRef.current.resume();
      setIsPausedQuick(false);
      recordingTimerRef.current = setInterval(() => {
        setRecordingDuration(prev => prev + 1);
      }, 1000);
    }
  };

  const formatDuration = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const handleDeleteFolder = async (id) => {
    try {
      await axios.delete(`${API}/folders/${id}`);
      setDeleteFolderConfirm(null);
      await fetchData();
    } catch (error) {
      console.error('Error deleting folder:', error);
      alert(error.response?.data?.detail || 'Failed to delete folder');
    }
  };

  const handleConfirmSuggestion = async (folderId, newName) => {
    setConfirmingFolderId(folderId);
    try {
      const body = newName ? { name: newName.trim() } : {};
      const response = await axios.post(`${API}/folders/confirm/${folderId}`, body);
      const storiesExtracted = response.data.stories_extracted || 0;
      setRenamingSuggestedId(null);
      await fetchData();
      if (storiesExtracted > 0) {
        setFolderCreatedToast(`Folder confirmed! ${storiesExtracted} ${storiesExtracted === 1 ? 'story' : 'stories'} added`);
        setTimeout(() => setFolderCreatedToast(null), 4000);
      }
    } catch (error) {
      console.error('Error confirming folder:', error);
    } finally {
      setConfirmingFolderId(null);
    }
  };

  const handleDismissSuggestion = async (folderId) => {
    try {
      await axios.delete(`${API}/folders/suggested/${folderId}`);
      await fetchData();
    } catch (error) {
      console.error('Error dismissing folder:', error);
      alert('Failed to dismiss folder. Please try again.');
    }
  };

  // Drag-and-drop handlers
  const handleDragStart = useCallback((event) => {
    setActiveId(event.active.id);
    setNestReady(false);
  }, []);

  const handleDragOver = useCallback((event) => {
    const { over } = event;
    const newOverId = (over && over.id !== event.active.id) ? over.id : null;

    setOverFolderId(prev => {
      if (prev !== newOverId) {
        // Over target changed — reset nest timer
        clearTimeout(nestTimerRef.current);
        setNestReady(false);
        if (newOverId) {
          nestTimerRef.current = setTimeout(() => setNestReady(true), 800);
        }
      }
      return newOverId;
    });
  }, []);

  const handleDragEnd = useCallback(async (event) => {
    const { active, over } = event;
    const wasNestReady = nestReady;
    setActiveId(null);
    setOverFolderId(null);
    setNestReady(false);
    clearTimeout(nestTimerRef.current);

    if (!over || active.id === over.id) return;

    // If held long enough → nest into subfolder
    if (wasNestReady) {
      handleNestFolder(active.id, over.id);
      return;
    }

    // Otherwise → reorder
    const currentFolders = folders.filter(f => !f.is_suggested && f.name !== 'Shared with Me' && !f.parent_folder_id);
    const activeFolder = currentFolders.find(f => f.id === active.id);
    const overFolder = currentFolders.find(f => f.id === over.id);
    if (!activeFolder || !overFolder) return;

    const oldIndex = currentFolders.findIndex(f => f.id === active.id);
    const newIndex = currentFolders.findIndex(f => f.id === over.id);
    if (oldIndex !== -1 && newIndex !== -1) {
      const newOrder = arrayMove(currentFolders, oldIndex, newIndex);
      const folderIds = newOrder.filter(f => !f._isShared).map(f => f.id);
      try {
        await axios.put(`${API}/folders/reorder`, { folder_ids: folderIds });
        await fetchData();
      } catch (err) { console.error('Reorder failed:', err); }
    }
  }, [folders, nestReady]);

  const handleNestFolder = useCallback(async (childId, parentId) => {
    try {
      await axios.put(`${API}/folders/${childId}/parent`, { parent_folder_id: parentId });
      setFolderCreatedToast('Folder moved inside');
      setTimeout(() => setFolderCreatedToast(null), 3000);
      await fetchData();
    } catch (err) {
      console.error('Nest failed:', err);
      if (err.response?.data?.detail) {
        setFolderCreatedToast(err.response.data.detail);
        setTimeout(() => setFolderCreatedToast(null), 3000);
      }
    }
  }, []);

  // "Shared with Me" real folder — pinned at top with gold border
  const sharedWithMeFolder = folders.find(f => f.name === 'Shared with Me');
  const sharedWithMeCount = (() => {
    if (sharedWithMeFolder && sharedWithMeFolder.story_count !== undefined) return sharedWithMeFolder.story_count;
    return experiences.filter(e => e.topic_folder === 'Shared with Me').length;
  })();

  // Shared-with-me collaborative folders as regular tiles (show all, not just those with stories)
  const sharedAsRegular = sharedFolders
    .map(sf => ({
      id: sf.folder_id,
      name: sf.folder_name,
      _isShared: true,
      _shareId: sf.share_id,
      _ownerName: sf.owner_name,
      _ownerId: sf.owner_id,
      _storyCount: sf.story_count || 0,
      _collaborators: sf.collaborators || [],
      description: sf.description || '',
      created_at: sf.created_at || '',
      updated_at: sf.updated_at || ''
    }));

  // Combine owned folders and shared folders, then sort together
  // Build subfolder map: parent_id -> [child folders]
  const subfolderMap = {};
  folders.forEach(f => {
    if (f.parent_folder_id) {
      if (!subfolderMap[f.parent_folder_id]) subfolderMap[f.parent_folder_id] = [];
      subfolderMap[f.parent_folder_id].push(f);
    }
  });

  const ownedFoldersBase = folders.filter(f => !f.is_suggested && f.name !== 'Shared with Me' && !f.parent_folder_id);
  const allRegularFolders = [...ownedFoldersBase, ...sharedAsRegular].sort((a, b) => {
    if (sortBy === 'count') {
      const countA = a._isShared ? (a._storyCount || 0) : (mySharedInfo[a.id]?.total_story_count ?? getStoryCount(a.name));
      const countB = b._isShared ? (b._storyCount || 0) : (mySharedInfo[b.id]?.total_story_count ?? getStoryCount(b.name));
      return countB - countA;
    }
    if (sortBy === 'recent') return new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0);
    if (sortBy === 'shared') {
      const aShared = a._isShared || mySharedInfo[a.id]?.has_collaborators ? 1 : 0;
      const bShared = b._isShared || mySharedInfo[b.id]?.has_collaborators ? 1 : 0;
      if (bShared !== aShared) return bShared - aShared;
      return a.name.localeCompare(b.name);
    }
    // Default: use custom sort_order if set, otherwise alphabetical
    const aOrder = a.sort_order ?? 999;
    const bOrder = b.sort_order ?? 999;
    if (aOrder !== bOrder) return aOrder - bOrder;
    return a.name.localeCompare(b.name);
  });

  // Legacy ownedFolders reference (for backward compatibility)
  const ownedFolders = allRegularFolders;
  const displayFolders = sortBy === 'shared'
    ? allRegularFolders.filter(f => f._isShared || mySharedInfo[f.id]?.has_collaborators)
    : allRegularFolders;
  const regularFolders = allRegularFolders;
  const sharedFoldersFiltered = allRegularFolders.filter(f => f._isShared || mySharedInfo[f.id]?.has_collaborators);
  const nonSharedFolders = allRegularFolders.filter(f => !f._isShared && !mySharedInfo[f.id]?.has_collaborators);
  const suggestedFolders = folders.filter(f => f.is_suggested).sort((a, b) => a.name.localeCompare(b.name));
  const hideUnsorted = sortBy === 'shared';

  // Helper to get collaborators for a folder
  const getCollaborators = (folder) => {
    if (folder._isShared && folder._collaborators) {
      return folder._collaborators;
    }
    if (mySharedInfo[folder.id]?.collaborators) {
      return mySharedInfo[folder.id].collaborators;
    }
    return [];
  };

  // Collaborator Avatars Component - Simple overlapping circles, no interaction
  const CollaboratorAvatars = ({ collaborators, isShared }) => {
    if (!collaborators || collaborators.length === 0) return null;
    
    // Sort so owner is first (will be on top with highest z-index)
    const sorted = [...collaborators].sort((a, b) => (b.is_owner ? 1 : 0) - (a.is_owner ? 1 : 0));
    const maxVisible = 4;
    const remaining = sorted.length - maxVisible;
    
    return (
      <div className="flex items-center">
        {sorted.slice(0, maxVisible).map((collab, idx) => (
          <div
            key={collab.user_id || idx}
            className={`w-6 h-6 rounded-full border-2 border-zinc-200 dark:border-zinc-800 flex items-center justify-center text-[10px] font-medium overflow-hidden ${
              isShared ? 'bg-accent/30 text-accent' : 'bg-primary/20 text-primary'
            }`}
            style={{ 
              zIndex: maxVisible - idx,
              marginLeft: idx === 0 ? 0 : '-8px' // 50% overlap
            }}
            title={collab.name}
          >
            {collab.picture ? (
              <img src={collab.picture} alt="" className="w-full h-full object-cover" />
            ) : (
              collab.name?.charAt(0)?.toUpperCase() || '?'
            )}
          </div>
        ))}
        {remaining > 0 && (
          <div 
            className={`w-6 h-6 rounded-full border-2 border-zinc-200 dark:border-zinc-800 flex items-center justify-center text-[10px] font-medium ${
              isShared ? 'bg-accent/10 text-accent' : 'bg-primary/10 text-primary'
            }`}
            style={{ marginLeft: '-8px', zIndex: 0 }}
          >
            +{remaining}
          </div>
        )}
      </div>
    );
  };

  return (
    <motion.div
      variants={getPageVariants(direction)}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={pageTransition}
      className="min-h-screen pt-20 md:pt-24 pb-12"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Title */}
        <div className="mb-4 md:mb-6">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-medium tracking-tight text-foreground mb-1" data-testid="stories-page-title">Stories</h1>
          <p className="text-sm md:text-base text-muted-foreground leading-relaxed">Your meaningful moments, organized by topic</p>
        </div>

        {/* Quick Add Story Box - below title, above icons */}
        <div className="max-w-md mb-4 md:mb-6" data-testid="quick-add-story-box">
          <div className="bg-card border-2 border-dashed border-primary/30 hover:border-primary/50 rounded-xl p-4 transition-colors">
            <div className="relative">
              <textarea
                value={quickStory}
                onChange={(e) => updateQuickStory(e.target.value)}
                placeholder={isTranscribingQuick ? "Transcribing audio..." : "Quick add a story..."}
                disabled={isTranscribingQuick}
                className="w-full bg-transparent border-none text-base md:text-lg font-serif leading-relaxed focus:outline-none resize-none min-h-[80px] max-h-[150px] placeholder:text-muted-foreground/50 disabled:opacity-50 pr-14"
                rows={4}
                data-testid="quick-story-textarea"
              />
              {/* Mic + Image buttons - bottom right */}
              <div className="absolute bottom-1 right-1 flex items-center space-x-1">
                {isRecordingQuick && (
                  <span className="text-xs text-red-500 font-mono font-medium tabular-nums" data-testid="quick-recording-timer">
                    {formatDuration(recordingDuration)}
                  </span>
                )}
                {!isRecordingQuick && !isTranscribingQuick && (
                  <>
                    <label className="p-2.5 rounded-full text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors cursor-pointer" data-testid="stories-quick-image-upload" title="Upload images">
                      <ImagePlus className="w-5 h-5" />
                      <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => {
                        const files = Array.from(e.target.files || []);
                        const remaining = 10 - quickImages.length;
                        const toAdd = files.slice(0, remaining).map(f => ({ file: f, preview: URL.createObjectURL(f) }));
                        if (toAdd.length > 0) setQuickImages(prev => [...prev, ...toAdd]);
                        e.target.value = '';
                      }} />
                    </label>
                  </>
                )}
                {isRecordingQuick ? (
                  <div className="flex items-center space-x-1">
                    {/* Pause/Resume button */}
                    <button
                      onClick={isPausedQuick ? resumeQuickRecording : pauseQuickRecording}
                      className={`p-2 rounded-full ${isPausedQuick ? 'bg-accent text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'} transition-colors`}
                      data-testid="quick-story-pause-record"
                      title={isPausedQuick ? "Resume recording" : "Pause recording"}
                    >
                      {isPausedQuick ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
                    </button>
                    {/* Stop button */}
                    <button
                      onClick={stopQuickRecording}
                      className="p-2.5 rounded-full bg-red-500 text-white animate-pulse-strong shadow-lg shadow-red-500/40"
                      data-testid="quick-story-stop-record"
                      title="Stop recording"
                    >
                      <Square className="w-5 h-5" />
                    </button>
                  </div>
                ) : isTranscribingQuick ? (
                  <ProgressBar progress={transcriptionProgress} label="Transcribing" variant="accent" />
                ) : (
                  <button
                    onClick={startQuickRecording}
                    className="p-2.5 rounded-full text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
                    data-testid="quick-story-record"
                    title="Record audio"
                  >
                    <Mic className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>
            {/* Audio attachment indicator */}
            {quickAudioBlob && !isRecordingQuick && !isTranscribingQuick && (
              <div className="flex items-center space-x-2 pt-1 pb-1">
                <span className="text-xs text-accent flex items-center space-x-1">
                  <Mic className="w-3 h-3" />
                  <span>Audio attached</span>
                </span>
                <button
                  onClick={() => setQuickAudioBlob(null)}
                  className="text-xs text-muted-foreground hover:text-red-400 transition-colors"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            )}
            {quickImages.length > 0 && (
              <div className="flex items-center gap-2 pt-2 pb-1 flex-wrap" data-testid="stories-quick-images-preview">
                {quickImages.map((img, idx) => (
                  <div key={img.preview || `img-${idx}`} className="relative group w-14 h-14 rounded-lg overflow-hidden border border-border">
                    <img src={img.preview} alt="" className="w-full h-full object-cover" />
                    <button
                      onClick={() => { URL.revokeObjectURL(img.preview); setQuickImages(prev => prev.filter((_, i) => i !== idx)); }}
                      className="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full w-4 h-4 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`stories-remove-quick-image-${idx}`}
                    >
                      <X className="w-2.5 h-2.5" />
                    </button>
                  </div>
                ))}
                {quickImages.length < 10 && (
                  <label className="w-14 h-14 rounded-lg border-2 border-dashed border-border hover:border-primary/50 flex items-center justify-center text-muted-foreground hover:text-primary transition-colors cursor-pointer">
                    <ImagePlus className="w-4 h-4" />
                    <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      const remaining = 10 - quickImages.length;
                      const toAdd = files.slice(0, remaining).map(f => ({ file: f, preview: URL.createObjectURL(f) }));
                      if (toAdd.length > 0) setQuickImages(prev => [...prev, ...toAdd]);
                      e.target.value = '';
                    }} />
                  </label>
                )}
              </div>
            )}
            {/* Save progress bar */}
            {isSaving && (
              <div className="pt-2" data-testid="stories-save-progress">
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-1.5 bg-muted/30 rounded-full overflow-hidden">
                    <div className="h-full bg-primary rounded-full transition-all duration-300" style={{ width: `${saveProgress}%` }} />
                  </div>
                  <span className="text-xs font-bold text-muted-foreground tabular-nums">{saveProgress}%</span>
                </div>
              </div>
            )}
            {(quickStory.trim() || quickAudioBlob || quickImages.length > 0) && !isSaving && (
              <div className="pt-2 border-t border-border">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-muted-foreground">
                    Saves to Unsorted Stories — AI will auto-sort
                  </span>
                  <button
                    onClick={handleQuickStory}
                    disabled={(!quickStory.trim() && !quickAudioBlob && quickImages.length === 0) || isAddingStory || isAnalyzing || isTranscribingQuick}
                    className="text-xs bg-primary text-white rounded-full px-3 py-1.5 font-medium hover:bg-primary/80 transition-colors disabled:opacity-50 flex items-center space-x-1 active:scale-95"
                    data-testid="quick-story-save-button"
                  >
                    {isAnalyzing ? <Sparkles className="w-3 h-3 animate-spin" /> : isAddingStory ? <Sparkles className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                    <span>{isAnalyzing ? 'Checking...' : 'Save'}</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* No suggestions message */}
        {noSuggestionsMsg && (
          <div className="mb-4 flex items-center space-x-2 text-sm text-muted-foreground bg-muted rounded-xl px-4 py-3">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>{noSuggestionsMsg}</span>
          </div>
        )}

        {/* Icons row */}
        <div className="flex items-center justify-between mb-6 md:mb-8">
          <div className="flex items-center space-x-2 md:space-x-3">
            {/* View Toggle */}
            <div className="flex items-center bg-card border border-border rounded-full p-1" data-testid="view-toggle">
              <button
                onClick={() => handleViewModeChange('compact')}
                className={`p-2 rounded-full transition-all ${viewMode === 'compact' ? 'bg-primary text-white' : 'text-muted-foreground hover:text-foreground'}`}
                data-testid="view-compact-button"
                title="List View"
              >
                <List className="w-4 h-4" />
              </button>
              <button
                onClick={() => handleViewModeChange('grid')}
                className={`p-2 rounded-full transition-all ${viewMode === 'grid' ? 'bg-primary text-white' : 'text-muted-foreground hover:text-foreground'}`}
                data-testid="view-grid-button"
                title="Grid View"
              >
                <LayoutGrid className="w-4 h-4" />
              </button>
            </div>
            {/* AI Suggest - icon only */}
            <button
              onClick={handleGenerateSuggestions}
              disabled={isGeneratingSuggestions}
              className="p-2.5 rounded-full bg-accent/10 text-accent hover:bg-accent/20 border border-accent/30 transition-all disabled:opacity-50"
              data-testid="get-ai-suggestions-button"
              title="Get AI Suggestions"
            >
              <Sparkles className={`w-5 h-5 ${isGeneratingSuggestions ? 'animate-pulse' : ''}`} />
            </button>
            <div className="relative" ref={addMenuRef}>
              <button
                onClick={() => setShowAddMenu(!showAddMenu)}
                className="bg-primary text-white hover:bg-primary/80 rounded-full px-3 md:px-6 py-2 md:py-3 text-sm md:text-base font-medium transition-all duration-300 shadow-sm hover:shadow-md active:scale-95 flex items-center space-x-1 md:space-x-2"
                data-testid="add-folder-button"
              >
                <PlusCircle className="w-4 h-4 md:w-5 md:h-5" />
                <span className="hidden sm:inline">New</span>
                <span className="sm:hidden">New</span>
              </button>
              {showAddMenu && (
                <div className="absolute top-full mt-1 right-0 bg-card-solid border border-border rounded-xl shadow-lg z-50 py-1 min-w-[170px]">
                  <button
                    onClick={() => { setNewFolderType('topic'); setShowNewFolder(true); setShowAddMenu(false); }}
                    className="w-full text-left px-4 py-2.5 text-sm text-foreground hover:bg-secondary/50 transition-colors flex items-center space-x-3"
                    data-testid="add-topic-option"
                  >
                    <FolderOpen className="w-4 h-4 text-primary" />
                    <span>New Topic</span>
                  </button>
                  <button
                    onClick={() => { setNewFolderType('event'); setShowNewFolder(true); setShowAddMenu(false); }}
                    className="w-full text-left px-4 py-2.5 text-sm text-foreground hover:bg-secondary/50 transition-colors flex items-center space-x-3"
                    data-testid="add-event-option"
                  >
                    <FolderOpen className="w-4 h-4 text-primary" />
                    <span>New Event</span>
                  </button>
                </div>
              )}
            </div>
          </div>
          
          {/* Sort button - far right */}
          <div className="relative" ref={sortMenuRef}>
            <button
              onClick={() => setShowSortMenu(!showSortMenu)}
              className={`p-2.5 rounded-full transition-all ${showSortMenu || sortBy !== 'name' ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`}
              data-testid="sort-button"
              title="Sort folders"
            >
              <SlidersHorizontal className="w-6 h-6" />
            </button>
            {showSortMenu && (
              <div className="absolute top-full mt-1 right-0 bg-card-solid border border-border rounded-xl shadow-lg z-50 py-1 min-w-[170px]" data-testid="sort-menu">
                {[
                  { id: 'shared', label: 'Shared Folders' },
                  { id: 'name', label: 'Alphabetical' },
                  { id: 'count', label: 'Most Stories' },
                  { id: 'recent', label: 'Recently Edited' },
                ].map(opt => (
                  <button
                    key={opt.id}
                    onClick={() => { setSortBy(opt.id); setShowSortMenu(false); }}
                    className={`w-full text-left px-4 py-2 text-sm transition-colors ${sortBy === opt.id ? 'text-primary font-medium bg-primary/5' : 'text-foreground hover:bg-secondary/50'}`}
                    data-testid={`sort-option-${opt.id}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {showNewFolder && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`bg-card border rounded-2xl p-4 md:p-6 mb-6 md:mb-8 ${newFolderType === 'event' ? 'border-blue-200' : 'border-border'}`}
            data-testid="new-folder-form"
          >
            <div className="flex items-center space-x-2 mb-3">
              <FolderOpen className="w-5 h-5 text-primary" />
              <span className="text-sm font-medium text-muted-foreground">
                {newFolderType === 'event' ? 'New Event' : 'New Topic'}
              </span>
            </div>
            <input
              type="text"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder={newFolderType === 'event' ? 'Event name...' : 'New topic name...'}
              className="w-full bg-card border-border rounded-xl px-4 py-3 text-base focus:border-primary focus:ring-primary/20"
              data-testid="new-folder-input"
            />
            <div className="flex justify-end space-x-2 mt-4">
              <button
                onClick={() => { setShowNewFolder(false); setNewFolderType('topic'); }}
                className="bg-transparent text-foreground hover:bg-secondary rounded-full px-4 md:px-6 py-2 transition-colors text-sm md:text-base"
                data-testid="cancel-folder-button"
              >
                Cancel
              </button>
              <button
                onClick={createFolder}
                className={`text-white rounded-full px-4 md:px-6 py-2 transition-colors text-sm md:text-base bg-primary hover:bg-primary/80`}
                data-testid="save-folder-button"
              >
                Create
              </button>
            </div>
          </motion.div>
        )}

        {/* Suggested folders count badge */}
        {suggestedFolders.length > 0 && !showSuggestions && (
          <motion.button
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            onClick={() => setShowSuggestions(true)}
            className="mb-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/30 hover:border-amber-500/50 transition-all text-sm"
          >
            <Sparkles className="w-3.5 h-3.5 text-amber-500" />
            <span className="text-foreground font-medium">{suggestedFolders.length} AI suggestion{suggestedFolders.length > 1 ? 's' : ''}</span>
            <ChevronDown className="w-3 h-3 text-muted-foreground" />
          </motion.button>
        )}

        {/* Getting Started card — shown when all folders are empty */}
        {!isLoadingData && totalStoryCount === 0 && folders.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 bg-primary/5 border border-primary/20 rounded-2xl p-5"
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="font-serif font-medium text-foreground mb-1">Ready to capture your first story?</h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-3">
                  Write in the box above, or head to <button onClick={() => navigate('/')} className="text-primary font-medium hover:underline">Journal</button> to record your thoughts.
                  <span className="inline-flex items-center gap-1 ml-1 text-primary/70"><Sparkles className="w-3 h-3" /> AI sorts them into folders automatically.</span>
                </p>
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => navigate('/')} className="inline-flex items-center gap-1.5 text-xs text-foreground bg-card px-3 py-2 rounded-full border border-border hover:border-primary/40 hover:text-primary transition-colors active:scale-95">
                    <BookOpen className="w-3.5 h-3.5" /> Write a journal entry
                  </button>
                  <button onClick={() => navigate('/', { state: { startRecording: true } })} className="inline-flex items-center gap-1.5 text-xs text-foreground bg-card px-3 py-2 rounded-full border border-border hover:border-primary/40 hover:text-primary transition-colors active:scale-95">
                    <Mic className="w-3.5 h-3.5" /> Record a voice note
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {isLoadingData ? (
          <div className={`${viewMode === 'compact' ? 'space-y-2' : 'grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4'}`}>
            {[...Array(6)].map((_, i) => (
              viewMode === 'compact' ? (
                <div key={i} className="flex items-center justify-between px-4 py-3 rounded-xl bg-card border border-border">
                  <div className="flex items-center space-x-3">
                    <div className="w-5 h-5 rounded bg-secondary animate-pulse" />
                    <div className="h-4 w-24 rounded bg-secondary animate-pulse" />
                  </div>
                  <div className="w-8 h-8 rounded-full bg-secondary animate-pulse" />
                </div>
              ) : (
                <div key={i} className="p-4 md:p-5 rounded-2xl bg-card border border-border h-40 flex flex-col">
                  <div className="w-6 h-6 rounded-lg bg-secondary animate-pulse mb-3" />
                  <div className="h-5 w-3/4 rounded bg-secondary animate-pulse mb-2" />
                  <div className="h-3 w-1/2 rounded bg-secondary/60 animate-pulse" />
                  <div className="mt-auto">
                    <div className="h-6 w-8 rounded bg-secondary animate-pulse" />
                  </div>
                </div>
              )
            ))}
          </div>
        ) : (
        <>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragOver={handleDragOver} onDragEnd={handleDragEnd}>
        <SortableContext items={displayFolders.map(f => f.id)} strategy={rectSortingStrategy}>
        <div className={`${viewMode === 'compact' ? 'space-y-1' : 'grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4'}`} data-testid="stories-folders-list">
          {/* Total Story Count Box - always first */}
          {viewMode === 'compact' ? (
            <div className="flex items-center justify-between px-4 py-3 bg-card border-2 border-primary/85 rounded-xl mb-2 relative" data-testid="total-story-count-box">
              {totalNewStories > 0 && (
                <span className="absolute -top-2.5 -right-2.5 z-40 bg-accent text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-lg pulse-slow pointer-events-none">{totalNewStories} new</span>
              )}
              <div className="flex items-center space-x-3">
                <span className="w-9 h-9 rounded-full bg-primary/10 border-2 border-primary/30 text-primary font-bold text-sm flex items-center justify-center">
                  {totalStoryCount}
                </span>
                <div>
                  <span className="text-sm font-medium text-foreground">Total Stories</span>
                  {streak && streak.current_streak > 0 && (
                    <p className="text-xs text-muted-foreground">{streak.current_streak} day streak</p>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="p-5 md:p-6 rounded-2xl bg-card border-2 border-primary/85 flex flex-col items-center justify-center text-center h-40 relative" data-testid="total-story-count-box">
              {totalNewStories > 0 && (
                <span className="absolute -top-2.5 -right-2.5 z-40 bg-accent text-white text-[11px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider shadow-lg pulse-slow pointer-events-none">{totalNewStories} new</span>
              )}
              <span className="w-16 h-16 rounded-full bg-primary/10 border-2 border-primary/30 text-primary font-bold text-2xl flex items-center justify-center mb-2">
                {totalStoryCount}
              </span>
              <h3 className="text-base font-serif font-medium text-foreground">Total Stories</h3>
              {streak && streak.current_streak > 0 && (
                <p className="text-xs text-muted-foreground mt-1">{streak.current_streak} day streak &middot; Best: {streak.longest_streak}</p>
              )}
            </div>
          )}

          {/* Shared with Me — real folder, pinned at top with gold border */}
          {sharedWithMeFolder && sharedWithMeCount > 0 && (
            viewMode === 'compact' ? (
              <div
                onClick={() => navigate(`/stories/${sharedWithMeFolder.id}`, { state: { folder: { id: sharedWithMeFolder.id, name: sharedWithMeFolder.name, color: sharedWithMeFolder.color, description: sharedWithMeFolder.description } } })}
                className="flex items-center justify-between px-4 py-3 bg-card border-2 border-accent/85 rounded-xl cursor-pointer group hover:border-accent/60 transition-all duration-200"
                data-testid="shared-with-me-folder"
              >
                <div className="flex items-center space-x-3">
                  <Users className="w-5 h-5 text-accent flex-shrink-0" />
                  <div>
                    <span className="text-sm font-serif font-medium text-foreground">Shared with Me</span>
                    <p className="text-xs text-muted-foreground">Stories & entries from others</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="w-8 h-8 rounded-full bg-accent/10 text-accent font-bold text-sm flex items-center justify-center">{sharedWithMeCount}</span>
                </div>
              </div>
            ) : (
              <div
                onClick={() => navigate(`/stories/${sharedWithMeFolder.id}`, { state: { folder: { id: sharedWithMeFolder.id, name: sharedWithMeFolder.name, color: sharedWithMeFolder.color, description: sharedWithMeFolder.description } } })}
                className="p-5 md:p-6 rounded-2xl bg-card border-2 border-accent/85 hover:border-accent/60 flex flex-col h-40 cursor-pointer transition-all duration-300 hover:shadow-lg group"
                data-testid="shared-with-me-folder"
              >
                <div className="flex items-start justify-between mb-2">
                  <FolderOpen className="w-6 h-6 text-accent flex-shrink-0" />
                </div>
                <h3 className="text-base font-serif font-medium text-foreground leading-tight">Shared with Me</h3>
                <p className="text-xs text-muted-foreground mt-1">Stories & entries from others</p>
                <div className="flex items-center justify-start mt-auto">
                  <span className="text-accent font-semibold text-lg">{sharedWithMeCount}</span>
                </div>
              </div>
            )
          )}

          {displayFolders.map((folder, folderIndex) => {
            const isShared = folder._isShared;
            const hasCollabs = mySharedInfo[folder.id]?.has_collaborators;
            const childFolders = subfolderMap[folder.id] || [];
            const hasChildren = childFolders.length > 0;
            // Folder color (fallback to primary via CSS)
            const folderColor = folder.color || null;
            // For shared folders, use _storyCount from the share data
            const ownStoryCount = isShared
                ? (folder._storyCount || 0)
                : hasCollabs
                    ? (mySharedInfo[folder.id]?.total_story_count ?? getStoryCount(folder.name))
                    : getStoryCount(folder.name);
            // Include subfolder story counts
            const childStoryCount = childFolders.reduce((sum, cf) => sum + (cf.story_count || 0), 0);
            const storyCount = ownStoryCount + childStoryCount;
            // hasNewStories — server provides has_new_stories flag, also check shared folders
            const hasNewStories = folder.has_new_stories || (isShared && folder._hasNewStories);
            // Use different route for shared folders vs own folders
            const navigateTo = isShared ? `/stories/shared/${folder.id}` : `/stories/${folder.id}`;
            const showCollabIcon = hasCollabs || isShared;
            const collaborators = getCollaborators(folder);
            
            // Check if this is the newly shared folder for highlighting
            const isNewlyShared = folder._isShared && folder.id === newSharedFolderId;
            
            const isBeingDraggedOver = overFolderId === folder.id && activeId !== folder.id;
            const isNestTarget = isBeingDraggedOver && nestReady;
            const tileStyle = folderColor ? {
              backgroundColor: hexToRgba(folderColor, 0.18),
              borderColor: isBeingDraggedOver ? folderColor : hexToRgba(folderColor, 0.5),
            } : {};

            return (
              <SortableFolderTile key={folder.id} id={folder.id} disabled={isShared}>
              <motion.div
                key={`${folder.id}-${sortBy}`}
                ref={isNewlyShared ? sharedFolderRef : null}
                initial={{ opacity: 0, y: 15 }}
                animate={isNewlyShared ? {
                  opacity: 1,
                  y: 0,
                  rotate: [0, -3, 3, -3, 3, 0],
                  transition: { duration: 0.5, ease: "easeInOut", delay: 0.3 }
                } : { opacity: 1, y: 0 }}
                whileHover={{ y: -4 }}
                transition={{ type: 'spring', stiffness: 300, delay: folderIndex * 0.04 }}
                data-testid={`folder-card-${folder.id}`}
                className="relative overflow-visible"
              >
                {/* Instruction tip for newly shared folder */}
                <AnimatePresence>
                  {isNewlyShared && showSharedFolderTip && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="absolute -top-14 left-1/2 -translate-x-1/2 z-20 pointer-events-none"
                    >
                      <div className="bg-white dark:bg-zinc-900 rounded-xl shadow-2xl border border-zinc-200 dark:border-zinc-700 px-3 py-2 whitespace-nowrap">
                        <p className="text-xs text-zinc-600 dark:text-zinc-300">
                          Tap to view folder <span className="font-medium text-primary">{newSharedFolderOwner || folder._ownerName}</span> shared with you
                        </p>
                      </div>
                      <div className="absolute top-full left-1/2 -translate-x-1/2 border-8 border-transparent border-t-white dark:border-t-zinc-900" />
                    </motion.div>
                  )}
                </AnimatePresence>
                
                {viewMode === 'compact' ? (
                  <SwipeToDelete 
                    onDelete={() => setDeleteFolderConfirm(folder.id)}
                    disabled={isShared}
                  >
                  <div
                    onClick={() => !activeId && navigate(navigateTo, { state: { folder: { id: folder.id, name: folder.name, color: folder.color, description: folder.description } } })}
                    className={`flex items-center justify-between px-4 py-3 rounded-xl border transition-all duration-200 cursor-pointer group active:bg-secondary/50 relative ${
                      isNestTarget ? 'ring-4 ring-accent scale-105 border-accent shadow-xl shadow-accent/30' :
                      isBeingDraggedOver ? 'ring-2 ring-primary scale-[1.02] border-primary' :
                      isNewlyShared ? 'border-accent border-2 ring-2 ring-accent/30' : folderColor ? '' : 'bg-card border-border hover:border-primary/50'
                    }`}
                    style={tileStyle}
                  >
                    {/* New badge for newly shared folders OR folders with new stories */}
                    {(isNewlyShared || hasNewStories) && (
                      <span className="absolute -top-2.5 -right-2.5 z-40 bg-accent text-white text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider shadow-lg pulse-slow pointer-events-none" data-testid={`folder-new-badge-${folder.id}`}>New</span>
                    )}
                    <div className="flex items-center space-x-3 min-w-0">
                      {/* Use collab icon (Users) for shared, FolderOpen for all others */}
                      {(isShared || showCollabIcon) ? <Users className="w-5 h-5 text-accent flex-shrink-0" /> : <FolderOpen className={`w-5 h-5 flex-shrink-0 ${folderColor ? '' : 'text-primary'}`} style={folderColor ? { color: folderColor } : {}} />}
                      <span className="text-sm font-serif font-medium text-foreground truncate" data-testid={`folder-name-${folder.id}`}>{folder.name}</span>
                      {hasChildren && <span className="text-[10px] text-muted-foreground bg-secondary px-1.5 py-0.5 rounded-full flex-shrink-0">{childFolders.length} folders</span>}
                      {isShared && (
                        <span className="text-[8px] text-muted-foreground bg-secondary px-1.5 py-0.5 rounded-full font-medium flex-shrink-0">{folder._ownerName}</span>
                      )}
                    </div>
                    <div className="flex items-center space-x-2 flex-shrink-0">
                      <span className={`w-8 h-8 rounded-full font-bold text-sm flex items-center justify-center ${(isShared || showCollabIcon) ? 'bg-accent/10 text-accent' : 'bg-primary/10 text-primary'}`} data-testid={`folder-count-badge-${folder.id}`}>{storyCount}</span>
                      {!isShared && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setDeleteFolderConfirm(folder.id); }}
                          className="hidden md:block text-muted-foreground hover:text-red-500 transition-colors p-1 opacity-0 group-hover:opacity-100"
                          data-testid={`delete-folder-button-${folder.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                  </SwipeToDelete>
                ) : (
                  <div className={`relative ${hasChildren ? 'pt-[6px] pr-[6px]' : ''}`}>
                    {/* Stacked cards behind for parent folders */}
                    {hasChildren && (
                      <>
                        <div className="absolute top-0 left-[6px] right-0 bottom-[8px] rounded-2xl bg-card border border-border opacity-40" />
                        <div className="absolute top-[3px] left-[3px] right-[3px] bottom-[4px] rounded-2xl bg-card border border-border opacity-60" />
                      </>
                    )}
                    <div
                      onClick={() => !activeId && navigate(navigateTo, { state: { folder: { id: folder.id, name: folder.name, color: folder.color, description: folder.description } } })}
                      className={`p-4 md:p-5 rounded-2xl border transition-all duration-300 hover:shadow-lg cursor-pointer group relative h-40 flex flex-col ${hasChildren ? 'shadow-sm z-[2]' : ''} ${
                        isNestTarget ? 'ring-4 ring-accent scale-110 border-accent shadow-xl shadow-accent/30' :
                        isBeingDraggedOver ? 'ring-2 ring-primary scale-105 border-primary' :
                        isNewlyShared ? 'border-accent border-2 ring-2 ring-accent/30' :
                        folderColor ? '' : 'bg-card border-border hover:border-primary/50'
                      }`}
                      style={tileStyle}
                    >
                      {/* Nest indicator */}
                      {isNestTarget && (
                        <span className="absolute inset-0 flex items-center justify-center z-40 pointer-events-none">
                          <span className="bg-accent text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg">Drop to nest inside</span>
                        </span>
                      )}
                      {/* New badge for newly shared folders OR folders with new stories */}
                      {(isNewlyShared || hasNewStories) && !isNestTarget && (
                        <span className="absolute -top-2.5 -right-2.5 z-40 bg-accent text-white text-[11px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider shadow-lg pulse-slow pointer-events-none" data-testid={`folder-new-badge-${folder.id}`}>New</span>
                      )}
                      <div className="flex items-start justify-between mb-3">
                        {/* Folder icon - use folder color if set */}
                        {(isShared || showCollabIcon) ? (
                          <Users className="w-6 h-6 text-accent flex-shrink-0" />
                        ) : (
                          <FolderOpen className={`w-6 h-6 flex-shrink-0 ${folderColor ? '' : 'text-primary'}`} style={folderColor ? { color: folderColor } : {}} />
                        )}
                        {/* Collaborator avatars - top right */}
                        {showCollabIcon && collaborators.length > 0 && (
                          <CollaboratorAvatars collaborators={collaborators} isShared={isShared || showCollabIcon} />
                        )}
                      </div>
                      <h3 className="text-base font-serif font-medium text-foreground line-clamp-2 leading-snug" data-testid={`folder-name-${folder.id}`}>
                        {folder.name}
                      </h3>
                      <div className="flex-1" />
                      <div className="flex items-center justify-between">
                        {/* Count - bottom left */}
                        <div className="flex items-baseline gap-1.5">
                          {hasChildren && (
                            <span className="text-xs text-muted-foreground">{childFolders.length} {childFolders.length === 1 ? 'folder' : 'folders'} ·</span>
                          )}
                          <span className={`font-semibold text-lg ${showCollabIcon ? 'text-accent' : (folderColor ? '' : 'text-primary')}`} style={folderColor && !showCollabIcon ? { color: folderColor } : {}} data-testid={`folder-count-badge-${folder.id}`}>
                            {storyCount}
                          </span>
                          <span className="text-xs text-muted-foreground ml-0.5">{storyCount === 1 ? 'story' : 'stories'}</span>
                        </div>
                        {/* Delete button - bottom right, hidden on mobile */}
                        {!isShared && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteFolderConfirm(folder.id);
                            }}
                            className={`hidden md:flex p-1.5 rounded-full transition-colors opacity-0 group-hover:opacity-100 ${
                              showCollabIcon
                                ? 'text-accent/60 hover:text-accent hover:bg-accent/10'
                                : 'text-primary/60 hover:text-primary hover:bg-primary/10'
                            }`}
                            data-testid={`delete-folder-button-${folder.id}`}
                            title="Delete folder"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
              </SortableFolderTile>
            );
          })}

          {/* Ghost tiles for AI-suggested folders */}
          {showSuggestions && suggestedFolders.map((folder, idx) => {
            const pendingCount = folder.pending_story_count || 0;
            const isRenaming = renamingSuggestedId === folder.id;
            return viewMode === 'compact' ? (
              <motion.div
                key={`suggested-${folder.id}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
              >
                <div className="flex items-center justify-between px-4 py-3 rounded-xl bg-card border-2 border-dashed border-amber-500/40 opacity-80 relative">
                  <div className="flex items-center space-x-3 min-w-0">
                    <Sparkles className="w-5 h-5 text-amber-500 flex-shrink-0" />
                    {isRenaming ? (
                      <input
                        autoFocus
                        type="text"
                        value={renamingSuggestedName}
                        onChange={(e) => setRenamingSuggestedName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleConfirmSuggestion(folder.id, renamingSuggestedName); if (e.key === 'Escape') setRenamingSuggestedId(null); }}
                        className="text-sm font-serif font-medium text-foreground bg-transparent border-b border-amber-500/50 focus:outline-none focus:border-amber-500 min-w-0"
                      />
                    ) : (
                      <span className="text-sm font-serif font-medium text-foreground truncate">{folder.name}</span>
                    )}
                    {pendingCount > 0 && <span className="text-[10px] text-amber-600 bg-amber-500/10 px-1.5 py-0.5 rounded-full flex-shrink-0">{pendingCount} pending</span>}
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <button onClick={() => handleConfirmSuggestion(folder.id)} disabled={confirmingFolderId === folder.id} className="w-7 h-7 rounded-full bg-green-500/10 text-green-600 hover:bg-green-500/20 flex items-center justify-center transition-colors disabled:opacity-50" title="Keep">
                      {confirmingFolderId === folder.id ? <Sparkles className="w-3.5 h-3.5 animate-pulse" /> : <Check className="w-3.5 h-3.5" />}
                    </button>
                    <button onClick={() => { setRenamingSuggestedId(folder.id); setRenamingSuggestedName(folder.name); }} className="w-7 h-7 rounded-full bg-secondary text-muted-foreground hover:text-foreground hover:bg-secondary/70 flex items-center justify-center transition-colors" title="Rename">
                      <Edit2 className="w-3 h-3" />
                    </button>
                    <button onClick={() => handleDismissSuggestion(folder.id)} className="w-7 h-7 rounded-full bg-secondary text-muted-foreground hover:text-red-500 hover:bg-red-500/10 flex items-center justify-center transition-colors" title="Dismiss">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key={`suggested-${folder.id}`}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
              >
                <div className="p-4 md:p-5 rounded-2xl bg-card border-2 border-dashed border-amber-500/40 h-40 flex flex-col relative opacity-80 hover:opacity-100 transition-opacity">
                  {/* Sparkle badge */}
                  <span className="absolute -top-2 -right-2 z-10 bg-amber-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                    <Sparkles className="w-2.5 h-2.5" /> AI
                  </span>
                  <div className="flex items-start justify-between mb-2">
                    <FolderOpen className="w-6 h-6 text-amber-500 flex-shrink-0" />
                  </div>
                  {isRenaming ? (
                    <input
                      autoFocus
                      type="text"
                      value={renamingSuggestedName}
                      onChange={(e) => setRenamingSuggestedName(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleConfirmSuggestion(folder.id, renamingSuggestedName); if (e.key === 'Escape') setRenamingSuggestedId(null); }}
                      className="text-base font-serif font-medium text-foreground bg-transparent border-b border-amber-500/50 focus:outline-none focus:border-amber-500 flex-1 mb-1"
                    />
                  ) : (
                    <h3 className="text-base font-serif font-medium text-foreground line-clamp-2 leading-tight flex-1">{folder.name}</h3>
                  )}
                  {pendingCount > 0 && (
                    <p className="text-xs text-amber-600 mt-1">{pendingCount} {pendingCount === 1 ? 'story' : 'stories'} pending</p>
                  )}
                  <div className="flex items-center gap-2 mt-auto">
                    <button
                      onClick={() => handleConfirmSuggestion(folder.id, isRenaming ? renamingSuggestedName : undefined)}
                      disabled={confirmingFolderId === folder.id}
                      className="flex-1 bg-green-500/10 text-green-600 hover:bg-green-500/20 rounded-lg px-2 py-1.5 text-xs font-medium flex items-center justify-center gap-1 transition-colors disabled:opacity-50"
                    >
                      {confirmingFolderId === folder.id ? <Sparkles className="w-3 h-3 animate-pulse" /> : <Check className="w-3 h-3" />}
                      <span>Keep</span>
                    </button>
                    <button
                      onClick={() => { setRenamingSuggestedId(folder.id); setRenamingSuggestedName(folder.name); }}
                      className="w-8 h-8 rounded-lg bg-secondary text-muted-foreground hover:text-foreground hover:bg-secondary/70 flex items-center justify-center transition-colors"
                      title="Rename"
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => handleDismissSuggestion(folder.id)}
                      className="w-8 h-8 rounded-lg bg-secondary text-muted-foreground hover:text-red-500 hover:bg-red-500/10 flex items-center justify-center transition-colors"
                      title="Dismiss"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </motion.div>
            );
          })}

          {/* Unsorted Stories Folder - hidden when sorting by shared */}
          {!hideUnsorted && (viewMode === 'compact' ? (
            <div
              onClick={() => navigate('/stories/unsorted', { state: { folder: { id: 'unsorted', name: 'Unsorted Stories', description: 'Stories not yet sorted into a folder' } } })}
              className={`flex items-center justify-between px-4 py-3 rounded-xl bg-card border-2 transition-all duration-200 cursor-pointer group ${
                unsortedCount > 0 ? 'border-amber-700/30 bg-amber-700/5' : 'border-border'
              }`}
              data-testid="unsorted-stories-folder"
            >
              <div className="flex items-center space-x-3 min-w-0">
                <FolderOpen className={`w-5 h-5 flex-shrink-0 ${unsortedCount > 0 ? 'text-amber-700' : 'text-muted-foreground'}`} />
                <span className="text-sm font-serif font-medium text-foreground">Unsorted Stories</span>
                {unsortedCount > 0 && (
                  <span className="bg-amber-700 text-white text-[8px] font-bold px-1.5 py-0.5 rounded-full uppercase flex-shrink-0">
                    {unsortedCount}
                  </span>
                )}
              </div>
              <div className="flex items-center space-x-2">
                <span className={`w-8 h-8 rounded-full font-bold text-sm flex items-center justify-center ${
                  unsortedCount > 0 ? 'bg-amber-700/10 text-amber-700' : 'bg-primary/10 text-primary'
                }`}>{unsortedCount}</span>
              </div>
            </div>
          ) : (
            <div
              onClick={() => navigate('/stories/unsorted', { state: { folder: { id: 'unsorted', name: 'Unsorted Stories', description: 'Stories not yet sorted into a folder' } } })}
              className={`p-4 md:p-5 rounded-2xl bg-card border-2 transition-all duration-300 hover:shadow-lg cursor-pointer group relative h-40 flex flex-col ${
                unsortedCount > 0 ? 'border-amber-700/30 bg-amber-700/5' : 'border-border'
              }`}
              data-testid="unsorted-stories-folder"
            >
              {/* Sort badge - positioned like New badge */}
              {unsortedCount > 0 && (
                <span className="absolute -top-2 -right-2 z-30 bg-amber-700 text-white text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider shadow-md">Sort</span>
              )}
              <div className="flex items-start justify-between mb-2">
                <FolderOpen className={`w-6 h-6 flex-shrink-0 ${unsortedCount > 0 ? 'text-amber-700' : 'text-muted-foreground'}`} />
              </div>
              <h3 className="text-base font-serif font-medium text-foreground leading-tight flex-1">
                Unsorted Stories
              </h3>
              <div className="flex items-center justify-start mt-auto">
                <span className={`font-semibold text-lg ${unsortedCount > 0 ? 'text-amber-700' : 'text-primary'}`}>
                  {unsortedCount}
                </span>
              </div>
            </div>
          ))}

          {/* Add Custom Folder Box - always last */}
          {viewMode === 'compact' ? (
            showBottomNewFolder ? (
              <div className="flex items-center space-x-2 px-4 py-3 rounded-xl border-2 border-primary/50 bg-card-solid" data-testid="add-folder-inline-compact">
                <FolderOpen className="w-5 h-5 text-primary flex-shrink-0" />
                <input
                  id="bottom-folder-input"
                  type="text"
                  value={bottomFolderName}
                  onChange={(e) => setBottomFolderName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && bottomFolderName.trim()) {
                      handleCreateAndAssignFolder(bottomFolderName.trim(), 'bottom');
                      setBottomFolderName('');
                      setShowBottomNewFolder(false);
                    }
                    if (e.key === 'Escape') { setShowBottomNewFolder(false); setBottomFolderName(''); }
                  }}
                  placeholder="Folder name..."
                  className="flex-1 bg-transparent text-sm font-serif font-medium text-foreground placeholder:text-muted-foreground/50 focus:outline-none"
                  autoFocus
                  data-testid="inline-folder-name-input"
                />
                <button onClick={() => { if (bottomFolderName.trim()) { handleCreateAndAssignFolder(bottomFolderName.trim(), 'bottom'); setBottomFolderName(''); setShowBottomNewFolder(false); }}} className="text-xs bg-primary text-white rounded-full px-3 py-1 font-medium hover:bg-primary/80 disabled:opacity-50" disabled={!bottomFolderName.trim()} data-testid="inline-folder-save">Create</button>
                <button onClick={() => { setShowBottomNewFolder(false); setBottomFolderName(''); }} className="text-muted-foreground hover:text-foreground" title="Cancel"><X className="w-4 h-4" /></button>
              </div>
            ) : (
              <div
                onClick={() => { setShowBottomNewFolder(true); setTimeout(() => { const el = document.getElementById('bottom-folder-input'); if (el) el.focus(); }, 100); }}
                className="flex items-center space-x-3 px-4 py-3 rounded-xl border-2 border-dashed border-muted-foreground/30 text-muted-foreground hover:border-primary hover:text-primary transition-colors cursor-pointer"
                data-testid="add-folder-box-compact"
              >
                <PlusCircle className="w-5 h-5" />
                <span className="text-sm font-medium">Add Folder</span>
              </div>
            )
          ) : (
            showBottomNewFolder ? (
              <div className="p-5 md:p-6 rounded-2xl border-2 border-primary/50 bg-card-solid min-h-[120px] flex flex-col" data-testid="add-folder-inline-grid">
                <FolderOpen className="w-6 h-6 text-primary mb-3" />
                <input
                  id="bottom-folder-input"
                  type="text"
                  value={bottomFolderName}
                  onChange={(e) => setBottomFolderName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && bottomFolderName.trim()) {
                      handleCreateAndAssignFolder(bottomFolderName.trim(), 'bottom');
                      setBottomFolderName('');
                      setShowBottomNewFolder(false);
                    }
                    if (e.key === 'Escape') { setShowBottomNewFolder(false); setBottomFolderName(''); }
                  }}
                  placeholder="Folder name..."
                  className="w-full bg-transparent text-sm sm:text-base font-serif font-medium text-foreground placeholder:text-muted-foreground/50 focus:outline-none mb-3"
                  autoFocus
                  data-testid="inline-folder-name-input"
                />
                <div className="flex items-center justify-between mt-auto">
                  <button onClick={() => { setShowBottomNewFolder(false); setBottomFolderName(''); }} className="text-xs text-muted-foreground hover:text-foreground transition-colors">Cancel</button>
                  <button onClick={() => { if (bottomFolderName.trim()) { handleCreateAndAssignFolder(bottomFolderName.trim(), 'bottom'); setBottomFolderName(''); setShowBottomNewFolder(false); }}} className="text-xs bg-primary text-white rounded-full px-4 py-1.5 font-medium hover:bg-primary/80 disabled:opacity-50" disabled={!bottomFolderName.trim()} data-testid="inline-folder-save">Create</button>
                </div>
              </div>
            ) : (
              <div
                onClick={() => { setShowBottomNewFolder(true); setTimeout(() => { const el = document.getElementById('bottom-folder-input'); if (el) el.focus(); }, 100); }}
                className="p-5 md:p-6 rounded-2xl border-2 border-dashed border-muted-foreground/30 text-muted-foreground hover:border-primary hover:text-primary transition-colors flex flex-col items-center justify-center space-y-2 h-40 cursor-pointer"
                data-testid="add-folder-box"
              >
                <PlusCircle className="w-8 h-8" />
                <span className="text-sm font-medium">Add New Folder</span>
              </div>
            )
          )}
        </div>
        {/* Drag overlay — shows the dragged folder */}
        <DragOverlay>
          {activeId ? (
            <div className="w-40 h-40 rounded-2xl bg-card border-2 border-primary shadow-2xl flex items-center justify-center opacity-90">
              <FolderOpen className="w-8 h-8 text-primary" />
            </div>
          ) : null}
        </DragOverlay>
        </SortableContext>
        </DndContext>

        {/* Folder Created Toast */}
        </>
        )}
        {folderCreatedToast && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-primary text-white px-6 py-3 rounded-full shadow-lg z-50 text-sm font-medium"
            data-testid="folder-created-toast"
          >
            {folderCreatedToast}
          </motion.div>
        )}

        {showJournalPrompt && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
            data-testid="journal-prompt-modal"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-card-solid rounded-2xl p-6 max-w-md w-full shadow-2xl"
            >
              <div className="flex items-center space-x-3 mb-4">
                <div className="p-2 bg-accent/10 rounded-xl">
                  <Sparkles className="w-5 h-5 text-accent" />
                </div>
                <h3 className="text-lg font-serif font-medium text-foreground">Multiple stories detected</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
                This looks like it contains <span className="font-medium text-foreground">{storyCount} different stories</span>. Would you like to save it as a journal entry and let AI extract each story into the right folders?
              </p>
              <div className="space-y-3">
                <button
                  onClick={saveAsJournalEntry}
                  disabled={isAddingStory || isTranscribingQuick}
                  className="w-full bg-primary text-white hover:bg-primary/80 rounded-full px-5 py-3 font-medium transition-all flex items-center justify-center space-x-2 disabled:opacity-50"
                  data-testid="save-as-journal-button"
                >
                  <BookOpen className="w-4 h-4" />
                  <span>{isAddingStory ? 'Saving...' : 'Save as journal entry'}</span>
                </button>
                <button
                  onClick={saveAsQuickStory}
                  disabled={isAddingStory || isTranscribingQuick}
                  className="w-full bg-card border border-border text-foreground hover:bg-secondary rounded-full px-5 py-3 font-medium transition-all disabled:opacity-50"
                  data-testid="save-as-single-story-button"
                >
                  Save as single story anyway
                </button>
                <button
                  onClick={() => setShowJournalPrompt(false)}
                  className="w-full text-muted-foreground hover:text-foreground text-sm py-1 transition-colors"
                  data-testid="cancel-journal-prompt"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}

        {deleteFolderConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
            data-testid="delete-folder-confirmation-modal"
          >
            <div className="bg-card-solid rounded-2xl p-5 md:p-6 max-w-md w-full shadow-2xl">
              <h3 className="text-lg md:text-xl font-serif font-medium mb-3">Delete Topic?</h3>
              <p className="text-muted-foreground mb-6 text-sm md:text-base">This will delete all stories in this topic. This action cannot be undone.</p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setDeleteFolderConfirm(null)}
                  className="bg-transparent text-foreground hover:bg-secondary rounded-full px-6 py-2 transition-colors text-sm md:text-base"
                  data-testid="cancel-delete-folder-button"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDeleteFolder(deleteFolderConfirm)}
                  className="bg-red-500 text-white hover:bg-red-600 rounded-full px-6 py-2 transition-colors text-sm md:text-base"
                  data-testid="confirm-delete-folder-button"
                >
                  Delete
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}

export default StoriesPage;
